import { useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import { AppThemeProvider } from '@srichaitra/ui';
import { createAppStore, restoreSession, useAppDispatch } from '@srichaitra/store';

/**
 * Restores a session left over from a previous visit.
 *
 * Split into its own component because it has to run *inside* the Provider — it
 * dispatches — and a component that renders nothing is the standard way to hang
 * a start-up effect off the tree.
 *
 * The thunk is condition-gated on there being a stored token, so this is a
 * no-op for a first-time visitor rather than a guaranteed 401.
 */
function SessionBootstrap({ children }) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(restoreSession());
  }, [dispatch]);

  return children;
}

/**
 * Everything the application needs in scope, in one wrapper: the Material UI
 * theme, the Redux store, and the session restore.
 *
 * The store is created here rather than imported as a module singleton so a
 * test can render the tree with its own preloaded state, and so two stores
 * never share memory across test cases.
 */
export function AppProviders({ store, preloadedState, children }) {
  const appStore = useMemo(() => store ?? createAppStore(preloadedState), [store, preloadedState]);

  return (
    <AppThemeProvider>
      <Provider store={appStore}>
        <SessionBootstrap>{children}</SessionBootstrap>
      </Provider>
    </AppThemeProvider>
  );
}
